import { useState, useEffect, useMemo } from 'react'
import { createBox, successBox } from '../../utils/asciiBox'
import { useMailStore, type Email } from '../../stores/mailStore'
import './Contact.css'

interface ComposeForm {
  name: string
  email: string
  organization: string
  subject: string
  body: string
}

type Folder = 'inbox' | 'sent'
type View = 'list' | 'compose'

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || 'https://tradewars-server-156160925122.us-central1.run.app'

export default function Contact() {
  // Mail store for local replies
  const { localReplies, addReply } = useMailStore()

  // State
  const [emails, setEmails] = useState<Email[]>([])
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null)
  const [currentFolder, setCurrentFolder] = useState<Folder>('inbox')
  const [view, setView] = useState<View>('list')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [composeForm, setComposeForm] = useState<ComposeForm>({
    name: '',
    email: '',
    organization: '',
    subject: '',
    body: '',
  })
  const [replyMode, setReplyMode] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replying, setReplying] = useState(false)

  // Fetch emails on mount
  useEffect(() => {
    fetchEmails()
  }, [])

  const fetchEmails = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE}/api/v1/mail`)
      if (!response.ok) {
        throw new Error('Failed to fetch mail')
      }
      const data = await response.json()
      setEmails(data.emails || [])
    } catch (err) {
      console.error('Failed to fetch emails:', err)
      // Use mock data if API fails
      setEmails(getMockEmails())
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    // Validate form
    if (!composeForm.name || !composeForm.email || !composeForm.subject || !composeForm.body) {
      setError('ERROR: All fields except Organization are required')
      return
    }

    if (composeForm.body.length < 10) {
      setError('ERROR: Message too short. Please include more details.')
      return
    }

    setSending(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/api/v1/mail/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(composeForm),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send message')
      }

      setSuccessMessage(data.message || 'TRANSMISSION COMPLETE')
      setComposeForm({
        name: '',
        email: '',
        organization: '',
        subject: '',
        body: '',
      })

      // Return to inbox after a delay
      setTimeout(() => {
        setView('list')
        setSuccessMessage(null)
        fetchEmails()
      }, 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'TRANSMISSION ERROR')
    } finally {
      setSending(false)
    }
  }

  const handleSelectEmail = (email: Email) => {
    setSelectedEmail(email)
    setReplyMode(false)
    setReplyText('')
    // Mark as read (optimistic update)
    if (!email.read) {
      setEmails(prev =>
        prev.map(e => (e.id === email.id ? { ...e, read: true } : e))
      )
      // Also mark read on server
      fetch(`${API_BASE}/api/v1/mail/read/${email.folder}/${email.id}`, {
        method: 'POST',
      }).catch(() => {})
    }
  }

  const handleReply = async () => {
    if (!selectedEmail || replyText.length < 5) return

    setReplying(true)
    setError(null)

    try {
      // Find the original email (the one we're replying to, not an AI response)
      const originalEmailId = selectedEmail.inReplyTo || selectedEmail.id

      const response = await fetch(
        `${API_BASE}/api/v1/mail/reply/${selectedEmail.folder}/${selectedEmail.id}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: replyText }),
        }
      )

      // Handle non-JSON responses (like 404 pages)
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('TRANSMISSION ERROR: Mail service unavailable. Please try again later.')
      }

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send reply')
      }

      // 2-second delay for UX before showing the response
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Store both user reply and AI response locally (not in backend)
      const aiResponse = data.reply as Email
      addReply(originalEmailId, replyText, aiResponse)

      setReplyMode(false)
      setReplyText('')
      setSelectedEmail(aiResponse)
      setSuccessMessage('REPLY RECEIVED! Check your inbox.')

      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'TRANSMISSION ERROR')
    } finally {
      setReplying(false)
    }
  }

  // Combine backend emails with local AI responses
  const allEmails = useMemo(() => {
    const aiResponseEmails = localReplies.map(r => r.aiResponse)
    return [...emails, ...aiResponseEmails]
  }, [emails, localReplies])

  const filteredEmails = useMemo(() => {
    return allEmails
      .filter(e => e.folder === currentFolder)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [allEmails, currentFolder])

  const unreadCount = allEmails.filter(e => e.folder === 'inbox' && !e.read).length

  // Get thread replies for the selected email
  const threadReplies = useMemo(() => {
    if (!selectedEmail) return []
    const originalId = selectedEmail.inReplyTo || selectedEmail.id
    return localReplies.filter(
      r => r.originalEmailId === originalId || r.originalEmailId === selectedEmail.id
    )
  }, [selectedEmail, localReplies])

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
    })
  }

  // Compose View
  if (view === 'compose') {
    return (
      <div className="mail-app">
        <div className="mail-toolbar">
          <button onClick={() => setView('list')} className="toolbar-btn">
            {'<'} Back
          </button>
          <span className="toolbar-title">NEW MESSAGE</span>
          <button
            onClick={handleSend}
            disabled={sending}
            className="toolbar-btn send-btn"
          >
            {sending ? '◐ SENDING...' : '[ SEND ]'}
          </button>
        </div>

        {successMessage ? (
          <div className="mail-success">
            <pre>{successBox(
              'TRANSMISSION SUCCESSFUL',
              ['Your message has been sent.', 'We will respond within 24 hours.'],
              41
            )}</pre>
          </div>
        ) : (
          <div className="compose-form">
            {error && <div className="mail-error">{error}</div>}

            <div className="compose-field">
              <label>TO:</label>
              <input type="text" value="contact@personascience.com" disabled />
            </div>

            <div className="compose-field">
              <label>YOUR NAME:</label>
              <input
                type="text"
                value={composeForm.name}
                onChange={e => setComposeForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your name"
              />
            </div>

            <div className="compose-field">
              <label>YOUR EMAIL:</label>
              <input
                type="email"
                value={composeForm.email}
                onChange={e => setComposeForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="your@email.com"
              />
            </div>

            <div className="compose-field">
              <label>ORGANIZATION:</label>
              <input
                type="text"
                value={composeForm.organization}
                onChange={e => setComposeForm(prev => ({ ...prev, organization: e.target.value }))}
                placeholder="(Optional)"
              />
            </div>

            <div className="compose-field">
              <label>SUBJECT:</label>
              <input
                type="text"
                value={composeForm.subject}
                onChange={e => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Message subject"
              />
            </div>

            <div className="compose-field compose-body">
              <label>MESSAGE:</label>
              <textarea
                value={composeForm.body}
                onChange={e => setComposeForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Type your message here..."
                rows={8}
              />
            </div>
          </div>
        )}

        <div className="mail-statusbar">
          SECURE CHANNEL | AES-256 ENCRYPTION
        </div>
      </div>
    )
  }

  // List View (Main)
  return (
    <div className="mail-app">
      <div className="mail-toolbar">
        <button onClick={() => setView('compose')} className="toolbar-btn compose-btn">
          [ COMPOSE ]
        </button>
        <button onClick={fetchEmails} className="toolbar-btn" disabled={loading}>
          {loading ? '◐' : '↻'} REFRESH
        </button>
        <span className="toolbar-title">PSMAIL v1.0</span>
      </div>

      <div className="mail-content">
        {/* Folder List */}
        <div className="mail-folders">
          <div className="folder-header">FOLDERS</div>
          <div
            className={`folder-item ${currentFolder === 'inbox' ? 'active' : ''}`}
            onClick={() => {
              setCurrentFolder('inbox')
              setSelectedEmail(null)
            }}
          >
            {currentFolder === 'inbox' ? '▸' : ' '} INBOX
            {unreadCount > 0 && <span className="unread-badge">({unreadCount})</span>}
          </div>
          <div
            className={`folder-item ${currentFolder === 'sent' ? 'active' : ''}`}
            onClick={() => {
              setCurrentFolder('sent')
              setSelectedEmail(null)
            }}
          >
            {currentFolder === 'sent' ? '▸' : ' '} SENT
          </div>
        </div>

        {/* Email List & Viewer */}
        <div className="mail-main">
          {/* Email List */}
          <div className="mail-list">
            <div className="mail-list-header">
              <span className="col-star"></span>
              <span className="col-from">FROM</span>
              <span className="col-subject">SUBJECT</span>
              <span className="col-date">DATE</span>
            </div>
            <div className="mail-list-body">
              {loading ? (
                <div className="mail-loading">◐ Loading mail...</div>
              ) : filteredEmails.length === 0 ? (
                <div className="mail-empty">No messages in {currentFolder}</div>
              ) : (
                filteredEmails.map(email => (
                  <div
                    key={email.id}
                    className={`mail-item ${selectedEmail?.id === email.id ? 'selected' : ''} ${!email.read ? 'unread' : ''}`}
                    onClick={() => handleSelectEmail(email)}
                  >
                    <span className="col-star">{email.starred ? '★' : '☆'}</span>
                    <span className="col-from" title={email.from}>
                      {email.fromName || email.from}
                    </span>
                    <span className="col-subject" title={email.subject}>
                      {email.subject}
                    </span>
                    <span className="col-date">{formatDate(email.date)}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Email Viewer */}
          <div className="mail-viewer">
            {successMessage && (
              <div className="mail-success" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, background: 'var(--crt-bg)', padding: '1rem' }}>
                <pre>{successBox('TRANSMISSION COMPLETE', [successMessage], 38)}</pre>
              </div>
            )}
            {selectedEmail ? (
              <>
                <div className="viewer-header">
                  <div className="viewer-field">
                    <span className="field-label">From:</span>
                    <span className="field-value">
                      {selectedEmail.fromName} {'<'}{selectedEmail.from}{'>'}
                    </span>
                  </div>
                  <div className="viewer-field">
                    <span className="field-label">Date:</span>
                    <span className="field-value">
                      {new Date(selectedEmail.date).toLocaleString()}
                    </span>
                  </div>
                  <div className="viewer-field">
                    <span className="field-label">Subject:</span>
                    <span className="field-value">{selectedEmail.subject}</span>
                  </div>
                </div>
                <div className="viewer-actions">
                  <button
                    onClick={() => setReplyMode(!replyMode)}
                    className="toolbar-btn"
                  >
                    {replyMode ? '✕ CANCEL' : '↪ REPLY'}
                  </button>
                </div>
                {replyMode && (
                  <div className="reply-form">
                    <div className="reply-header">COMPOSE REPLY (max 500 chars)</div>
                    {error && <div className="mail-error">{error}</div>}
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                      placeholder="Type your reply to get an AI-generated response..."
                      rows={4}
                      maxLength={500}
                    />
                    <div className="reply-actions">
                      <span className="char-count">{replyText.length}/500</span>
                      <button
                        onClick={() => { setReplyMode(false); setReplyText(''); setError(null) }}
                        className="toolbar-btn"
                      >
                        CANCEL
                      </button>
                      <button
                        onClick={handleReply}
                        disabled={replying || replyText.length < 5}
                        className="toolbar-btn send-btn"
                      >
                        {replying ? '◐ SENDING...' : '[ SEND REPLY ]'}
                      </button>
                    </div>
                  </div>
                )}
                <div className="viewer-divider">────────────────────────────────────────</div>
                <div className="viewer-body">
                  <pre>{selectedEmail.body}</pre>

                  {/* Show thread replies (user reply + AI response) */}
                  {threadReplies.map((reply) => (
                    <div key={reply.id} className="thread-reply">
                      <div className="thread-divider">
                        ─── YOUR REPLY ({new Date(reply.userReplyDate).toLocaleDateString()}) ───
                      </div>
                      <pre className="thread-user-reply">{`> ${reply.userReplyText.split('\n').join('\n> ')}`}</pre>

                      <div className="thread-divider">
                        ─── RESPONSE ───
                      </div>
                      <pre className="thread-ai-response">{reply.aiResponse.body}</pre>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="viewer-empty">
                <pre>{createBox(
                  { width: 32, style: 'double', padding: 1, align: 'center' },
                  ['Select a message to read', '', 'Or click [COMPOSE] to', 'send a new message']
                )}</pre>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mail-statusbar">
        {filteredEmails.length} messages | {unreadCount} unread | SECURE CONNECTION
      </div>
    </div>
  )
}

// Mock emails for when API is unavailable
function getMockEmails(): Email[] {
  return [
    {
      id: '1',
      folder: 'inbox',
      from: 'ceo@acme-corp.com',
      fromName: 'Wile E. Coyote',
      to: 'you@personaos.local',
      subject: 'RE: Rocket-Powered Roller Skates Order #847362',
      body: `Dear Valued Customer,

Your order of 500 pairs of rocket-powered roller skates has been processed and is scheduled for delivery via Anvil Express.

Please note: ACME Corporation is not responsible for any cliff-related incidents, unexpected gravity situations, or roadrunner encounters.

Expected delivery: When you least expect it.

Sincerely,
Wile E. Coyote
CEO, ACME Corporation
"If it doesn't work, we'll refund your hospital bills"`,
      date: '1987-03-15T10:30:00Z',
      read: false,
      starred: true,
    },
    {
      id: '2',
      folder: 'inbox',
      from: 'it-dept@megacorp.com',
      fromName: 'IT Department',
      to: 'you@personaos.local',
      subject: 'URGENT: Memory Upgrade Required',
      body: `MEMO TO ALL EMPLOYEES

RE: Mandatory RAM Upgrade

Due to increasing computational demands, all workstations will be upgraded from 64KB to a whopping 640KB of RAM.

As Bill Gates himself said, "640K ought to be enough for anybody."

Please backup your files to floppy disk before the upgrade.

- IT Department
"Have you tried turning it off and on again?"`,
      date: '1987-02-28T14:15:00Z',
      read: false,
      starred: false,
    },
    {
      id: '3',
      folder: 'inbox',
      from: 'classified@gov.mil',
      fromName: '[CLASSIFIED]',
      to: 'you@personaos.local',
      subject: 'Project PERSONA - Status Update',
      body: `[TOP SECRET - EYES ONLY]

Subject: Project PERSONA Phase 3

Agent,

The identity matrix protocols are proceeding as planned. The new biometric authentication system has shown promising results in initial trials.

Remember: Trust no one. Verify everything. The future of identity security depends on it.

Destroy this message after reading.

- Control

P.S. The microfilm is in the usual place.`,
      date: '1987-01-20T09:00:00Z',
      read: true,
      starred: true,
    },
    {
      id: '4',
      folder: 'inbox',
      from: 'sysop@coolbbs.net',
      fromName: 'CoolBBS Sysop',
      to: 'you@personaos.local',
      subject: 'Welcome to CoolBBS! Your account is ready',
      body: `*~*~*~* WELCOME TO COOLBBS *~*~*~*

          ████████╗██╗  ██╗███████╗
          ╚══██╔══╝██║  ██║██╔════╝
             ██║   ███████║█████╗
             ██║   ██╔══██║██╔══╝
             ██║   ██║  ██║███████╗
             ╚═╝   ╚═╝  ╚═╝╚══════╝

        COOLEST BBS ON THE NET!

Your 2400 baud connection has been approved!

FEATURES:
- Door games (L.O.R.D., TradeWars 2002!)
- File downloads (5 per day limit)
- Message boards
- Online chat (if anyone else is on)

Remember: No warez, no phreaking, no exceptions.

Happy BBSing!
- SysOp Dave`,
      date: '1986-11-10T16:45:00Z',
      read: true,
      starred: false,
    },
    {
      id: '5',
      folder: 'inbox',
      from: 'future.self@temporal.net',
      fromName: 'You (from 2025)',
      to: 'you@personaos.local',
      subject: 'WARNING: Important message from the future',
      body: `Dear Past Self,

I'm contacting you from the year 2025. This is extremely urgent.

Whatever you do, DO NOT throw away your collection of:
- Star Wars action figures (still in packaging)
- Nintendo games
- Comic books

Also, invest in a company called "Apple." Trust me.

One more thing: In about 10 years, there will be something called "the internet." It's basically like a really big BBS but worse somehow.

Your Future Self

P.S. VHS wins the format war. Just kidding, it doesn't matter.`,
      date: '1986-08-05T11:30:00Z',
      read: true,
      starred: false,
    },
  ]
}
